import { Callout } from '@/components/patterns/callout';
import { SeveridadeBadge, StatusIncidenteBadge } from '@/components/patterns/incidente-badges';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { AvisoHomologacao } from '@/lib/incidentes';

export interface GrupoAviso {
  versao: string;
  aviso: AvisoHomologacao;
  /** A consulta dos incidentes falhou: o aviso fica genérico, ainda sem bloquear. */
  erro: string | null;
}

export interface PedidoAviso {
  grupos: GrupoAviso[];
  resolver: (confirmado: boolean) => void;
}

export function AvisoHomologacaoDialog({
  pedido,
  aoDecidir,
}: {
  pedido: PedidoAviso | null;
  aoDecidir: (confirmado: boolean) => void;
}) {
  const grupos = pedido?.grupos ?? [];
  const grave = grupos.some((grupo) => grupo.aviso.grave);
  const varias = grupos.length > 1;
  const alvo = grupos.length === 1 ? grupos[0].versao : `${grupos.length} versões`;

  return (
    <Dialog open={pedido !== null} onOpenChange={(aberto) => !aberto && aoDecidir(false)}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Homologar {alvo} com incidente em aberto?</DialogTitle>
          <DialogDescription>
            {varias
              ? 'Estas versões têm incidentes conhecidos que ainda não foram resolvidos. '
              : 'Esta versão tem incidentes conhecidos que ainda não foram resolvidos. '}
            Homologar não é bloqueado, mas a decisão fica sua.
          </DialogDescription>
        </DialogHeader>

        {grave ? (
          <Callout tom="erro" kicker="Atenção">
            Há incidente de severidade <strong>alta ou crítica</strong>. Confirme que não {varias ? 'afetam' : 'afeta'} quem vai receber {varias ? 'estas versões' : 'esta versão'}.
          </Callout>
        ) : null}

        <div className="flex flex-col gap-3">
          {grupos.map((grupo) => (
            <section key={grupo.versao} aria-label={`Incidentes de ${grupo.versao}`} className="flex flex-col gap-1.5">
              {varias ? <h3 className="font-mono text-body font-bold">{grupo.versao}</h3> : null}
              {grupo.erro ? (
                <Callout tom="alerta">Não foi possível conferir os incidentes desta versão: {grupo.erro}</Callout>
              ) : (
                <ul className="flex flex-col divide-y divide-border-subtle rounded-input border border-border">
                  {grupo.aviso.naoResolvidos.map((incidente) => (
                    <li key={incidente.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
                      <SeveridadeBadge severidade={incidente.severidade} />
                      <StatusIncidenteBadge status={incidente.status} />
                      <span className="min-w-0 flex-1 basis-48 break-words text-body">{incidente.titulo}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" autoFocus onClick={() => aoDecidir(false)}>
            Cancelar
          </Button>
          <Button variant={grave ? 'danger' : 'primary'} onClick={() => aoDecidir(true)}>
            Homologar mesmo assim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
